import React, {ReactNode, useLayoutEffect, useMemo, useState} from 'react';
import {classes, style} from './embedded-video-player.st.css';
import {
    doppeValidateYoutubeShortURL,
    enumIsEnumValue,
    enumReduce,
    Evaluable,
    evaluateFunction,
    evaluateWhenFunction,
    urlCreateQueryString,
    Values,
    VideoServices,
    videoServicesResolveVideoSource
} from "@wix/devzai-utils-common";
import {RatioMaintainer} from "../ratio-maintainer/ratio-maintainer";

export const EmbeddedVideoPlayerVideoType = {
    YouTube: 'YouTube',
    YouTubeShort: 'YouTubeShort',
    YoutubePlaylist: 'YoutubePlaylist',
    Vimeo: 'Vimeo',
    TikTok: 'TikTok',
} as const;

export type EmbeddedVideoPlayerVideoType = Values<typeof EmbeddedVideoPlayerVideoType>;

export const EmbeddedVideoPlayerSupportedVideoSources = enumReduce(VideoServices, [
    VideoServices.YoutubePlaylist,
    VideoServices.Youtube,
    VideoServices.Vimeo,
    VideoServices.TikTok,
])

export type EmbeddedVideoPlayerSupportedVideoSources = Values<typeof EmbeddedVideoPlayerSupportedVideoSources>;

export const EmbeddedVideoPlayer = React.memo(function EmbeddedVideoPlayer (props: EmbeddedVideoPlayer.Props) {
    const {
        videoUrl,
        muteVideo,
        autoplay,
        videoTitle,
        widthHeightRatio = embeddedVideoPlayerAutoRatioCalculationFunction,
        renderNotSupportedVideo = null,

        className,
        ...htmlAttributes
    } = props;

    const actionTitle = videoTitle;

    const videoServiceSource = useMemo(() => {
        return videoServicesResolveVideoSource(videoUrl);
    }, [videoUrl])

    const [hostname, setHostname] = useState<string>();
    useLayoutEffect(() => {
        setHostname(window.location.hostname)
    }, [])

    const videoService = videoServiceSource?.videoService;

    if (videoService && enumIsEnumValue(EmbeddedVideoPlayerSupportedVideoSources, videoService)) {

        const evaluatedWidthHeightRatio = evaluateWhenFunction(widthHeightRatio, {
            videoType: evaluateFunction(() => {
                switch (videoService) {
                    case VideoServices.Youtube: {
                        return doppeValidateYoutubeShortURL(videoUrl) ?
                            EmbeddedVideoPlayerVideoType.YouTubeShort :
                            EmbeddedVideoPlayerVideoType.YouTube;
                    }
                    case VideoServices.YoutubePlaylist: {
                        return EmbeddedVideoPlayerVideoType.YoutubePlaylist;
                    }
                    case VideoServices.Vimeo: {
                        return EmbeddedVideoPlayerVideoType.Vimeo;
                    }
                    case VideoServices.TikTok: {
                        return EmbeddedVideoPlayerVideoType.TikTok;
                    }
                }
            }),
        })

        return (
            <RatioMaintainer
                {...htmlAttributes}
                className={style(classes.root, className)}
                widthHeightRatio={evaluatedWidthHeightRatio}
                key={videoUrl}
            >
                {evaluateFunction(() => {
                    switch (videoService) {
                        case VideoServices.Youtube: {
                            const videoId = videoServiceSource.videoId;

                            return (
                                <iframe
                                    className={style(classes.iframe, {})}
                                    src={`https://www.youtube.com/embed/${videoId}?${urlCreateQueryString({
                                        autoplay: autoplay ? '1' : '0',
                                        mute: muteVideo ? '1' : '0',
                                        playsinline: '1',
                                        modestbranding: '1',
                                        origin: hostname,
                                        iv_load_policy: '3',
                                        rel: '0',
                                        start: '0',
                                        // cc_load_policy: '0',
                                        // fs: '0'
                                    }, false)}`}
                                    allow={`fullscreen; accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture`}
                                    frameBorder="0"
                                    title={actionTitle}
                                />
                            )
                        }
                        case VideoServices.YoutubePlaylist: {
                            const videoId = videoServiceSource.videoId;

                            return (
                                <iframe
                                    src={`https://www.youtube.com/embed/videoseries?${urlCreateQueryString({
                                        list: videoId,
                                        autoplay: autoplay ? '1' : '0',
                                        mute: muteVideo ? '1' : '0',
                                        playsinline: '1',
                                        modestbranding: '1',
                                        origin: hostname,
                                        iv_load_policy: '3',
                                        rel: '0',
                                        start: '0',
                                        // cc_load_policy: '0',
                                        // fs: '0'
                                    }, false)}`}
                                    title={actionTitle}
                                    frameBorder="0"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                    allowFullScreen
                                />
                            )
                        }
                        case VideoServices.Vimeo: {

                            const videoId = videoServiceSource.videoId;

                            return (
                                <iframe
                                    src={`https://player.vimeo.com/video/${videoId}?${urlCreateQueryString({
                                        autoplay: autoplay ? '1' : '0',
                                        muted: muteVideo ? '1' : '0',
                                        playsinline: '1',
                                        portrait: '0',
                                        title: '0',
                                        byline: '0',
                                        autopause: '0'
                                    }, false)}`}
                                    allow={`accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture`}
                                    frameBorder="0"
                                    title={actionTitle}
                                />
                            )
                        }
                        case VideoServices.TikTok: {
                            return (
                                <iframe
                                    src={`https://www.tiktok.com/player/v1/${videoServiceSource.videoId}?${urlCreateQueryString({
                                        autoplay: autoplay ? '1' : '0',
                                    }, false)}`}
                                    allow={`fullscreen; accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture`}
                                    frameBorder="0"
                                    title={actionTitle}
                                />
                            )
                        }
                    }
                })}
            </RatioMaintainer>
        )
    }

    return (
        <>
            {evaluateWhenFunction(renderNotSupportedVideo, videoUrl)}
        </>
    )
});

export namespace EmbeddedVideoPlayer {

    export type VideoInfo = {
        videoType: EmbeddedVideoPlayerVideoType;
    }

    export interface Props extends React.HTMLAttributes<any> {
        videoUrl: string;
        videoTitle?: string;
        muteVideo: boolean;
        autoplay: boolean;
        widthHeightRatio?: Evaluable<(videoInfo: VideoInfo) => number>;
        renderNotSupportedVideo?: Evaluable<(videoUrl?: string) => ReactNode>;

        children?: undefined;
    }
}

export function embeddedVideoPlayerAutoRatioCalculationFunction (videoInfo: EmbeddedVideoPlayer.VideoInfo) {
    switch (videoInfo.videoType) {
        case EmbeddedVideoPlayerVideoType.YouTube:
        case EmbeddedVideoPlayerVideoType.Vimeo: {
            return 16/9;
        }
        case EmbeddedVideoPlayerVideoType.YoutubePlaylist: {
            return 1;
        }
        case EmbeddedVideoPlayerVideoType.TikTok: {
            return 9 / 16;
        }
        case EmbeddedVideoPlayerVideoType.YouTubeShort: {
            return 453 / 804;
        }
    }
}